import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRScanner from '../components/QRScanner';
import { eventApi } from '../services/api';
import { KilobaRacingGameComponent } from '../components/KilobaRacingGameComponent';
import NotificationModal from '../components/NotificationModal';
import { useAuth } from '../contexts/AuthContext';
import EnhancedLoginModal from '../components/EnhancedLoginModal';
import './EventQRScanPage.css';

interface EventInfo {
  eventName: string;
  location: string;
  startTime: string;
  endTime: string;
  description?: string;
}

interface JoinEventResult {
  success: boolean;
  message: string;
  gameSessionId?: string;
  eventInfo?: EventInfo;
}

const EventQRScanPage: React.FC = () => {
  const navigate = useNavigate();
  const { token: urlToken } = useParams<{ token: string }>();
  const { isAuthenticated, user, isLoading } = useAuth();
  const [scanning, setScanning] = useState(true);
  const [loading, setLoading] = useState(false);
  const [joinResult, setJoinResult] = useState<JoinEventResult | null>(null);
  const [error, setError] = useState<string>('');
  const [gameMode, setGameMode] = useState(false);
  const [gameSessionId, setGameSessionId] = useState<string>('');
  
  // Authentication states
  const [pendingToken, setPendingToken] = useState<string>('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Event attempt tracking
  const [remainingAttempts, setRemainingAttempts] = useState<number>(3);
  const [currentAttempt, setCurrentAttempt] = useState<number>(1);
  const [totalAttempts, setTotalAttempts] = useState<number>(3);
  const [currentHighScore, setCurrentHighScore] = useState<number>(0);
  
  // Notification modal state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  
  // Final voucher message for completion screen
  const [finalVoucherMessage, setFinalVoucherMessage] = useState<string>('');

  // Handle URL token on component mount
  useEffect(() => {
    if (urlToken) {
      // Wait for authentication state to be determined
      if (isLoading) {
        // Still checking auth, wait
        return;
      }
      
      if (!isAuthenticated) {
        // User is not authenticated, save token and show login modal
        setPendingToken(urlToken);
        setShowLoginModal(true);
        setScanning(false);
      } else {
        // User is authenticated, proceed with join event
        setScanning(false);
        setLoading(true);
        processEventJoin(urlToken);
      }
    }
  }, [urlToken, isAuthenticated, isLoading]);

  // Handle authentication completion
  useEffect(() => {
    if (isAuthenticated && pendingToken) {
      // User just logged in and we have a pending token
      setShowLoginModal(false);
      setLoading(true);
      processEventJoin(pendingToken);
      setPendingToken(''); // Clear pending token
    }
  }, [isAuthenticated, pendingToken]);

  // Separate function to process event join without auth checks
  const processEventJoin = async (qrData: string) => {
    setLoading(true);
    setScanning(false);
    
    try {
      // Parse QR data để lấy token
      let qrToken = '';
      
      // Kiểm tra nếu là URL từ QR code (có thể là link trực tiếp hoặc URL từ camera)
      if (qrData.includes('/join-event/')) {
        // Nếu là full URL, extract token từ URL path
        const urlParts = qrData.split('/join-event/');
        if (urlParts.length > 1) {
          qrToken = urlParts[1].split('?')[0]; // Lấy token và bỏ query params nếu có
        }
      } else if (qrData.includes('://')) {
        // Nếu là URL khác, thử parse query parameter
        try {
          const urlParams = new URL(qrData);
          qrToken = urlParams.searchParams.get('token') || '';
        } catch (e) {
          // Nếu không parse được URL, thử extract từ cuối URL
          qrToken = qrData.split('/').pop() || '';
        }
      } else if (qrData.includes(':')) {
        // Nếu là format sessionId:token (legacy format)
        qrToken = qrData;
      } else {
        // Nếu là token trực tiếp (fallback)
        qrToken = qrData;
      }

      if (!qrToken) {
        throw new Error('Không tìm thấy token trong mã QR');
      }

      // Gọi API join event với qrToken
      const result = await eventApi.verifyAndJoinEvent(qrToken);
      
      if (result.success) {
        setJoinResult(result);
        setGameSessionId(result.gameSessionId || '');
        
        // Update attempt information if provided
        if (result.attemptInfo) {
          setCurrentAttempt(result.attemptInfo.attemptNumber || 0);
          setRemainingAttempts(result.attemptInfo.remainingAttempts || 3);
          if (result.attemptInfo.totalAttempts) {
            setTotalAttempts(result.attemptInfo.totalAttempts);
          }
          // Try to get current high score from various possible fields
          if ((result.attemptInfo as any).currentHighestScore !== undefined) {
            setCurrentHighScore((result.attemptInfo as any).currentHighestScore);
          } else if ((result.attemptInfo as any).previousHighestScore !== undefined) {
            setCurrentHighScore((result.attemptInfo as any).previousHighestScore);
          }
        } else {
          // Default values if no attempt info provided
          setCurrentAttempt(0);
          setRemainingAttempts(3);
          setTotalAttempts(3);
          setCurrentHighScore(0);
        }
        
        // Chuyển sang chế độ game
        setGameMode(true);
      } else {
        // Sử dụng trực tiếp message từ server response
        setError(result.message || 'Có lỗi xảy ra khi tham gia sự kiện');
      }
    } catch (err: any) {
      let errorMessage = '';
      
      // Ưu tiên sử dụng message từ server response
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message === 'Authentication required. Please login first.' || 
          err.response?.status === 401) {
        errorMessage = 'Bạn cần đăng nhập để tham gia sự kiện. Vui lòng đăng nhập và thử lại.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Không tìm thấy sự kiện. Mã QR có thể đã hết hạn.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Mã QR không hợp lệ hoặc đã hết hạn.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Lỗi máy chủ. Vui lòng thử lại sau.';
      } else if (err.message?.toLowerCase().includes('network') || 
                 err.message?.toLowerCase().includes('fetch')) {
        errorMessage = 'Lỗi kết nối mạng. Vui lòng kiểm tra internet và thử lại.';
      } else {
        errorMessage = err.message || 'Có lỗi xảy ra khi tham gia sự kiện. Vui lòng thử lại.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleScanSuccess = async (qrData: string) => {
    // Check authentication first
    if (!isAuthenticated) {
      // Save the token and show login modal
      setPendingToken(qrData);
      setShowLoginModal(true);
      setScanning(false);
      setLoading(false);
      return;
    }

    // User is authenticated, proceed with event join
    await processEventJoin(qrData);
  };

  const handleScanError = (error: string) => {
    console.error('QR Scan error:', error);
    // Chuyển đổi thông báo lỗi sang tiếng Việt
    let vietnameseError = '';
    
    if (error.toLowerCase().includes('camera') || error.toLowerCase().includes('permission')) {
      vietnameseError = 'Không thể truy cập camera. Vui lòng cấp quyền truy cập camera và thử lại.';
    } else if (error.toLowerCase().includes('not found') || error.toLowerCase().includes('no device')) {
      vietnameseError = 'Không tìm thấy camera trên thiết bị của bạn.';
    } else if (error.toLowerCase().includes('invalid') || error.toLowerCase().includes('decode')) {
      vietnameseError = 'Mã QR không hợp lệ hoặc không đọc được. Vui lòng thử lại.';
    } else if (error.toLowerCase().includes('network') || error.toLowerCase().includes('connection')) {
      vietnameseError = 'Lỗi kết nối mạng. Vui lòng kiểm tra internet và thử lại.';
    } else {
      vietnameseError = `Lỗi quét mã QR: ${error}`;
    }
    
    setError(vietnameseError);
  };

  const handleGameEnd = async (result: any) => {

    try {
      const responseData = (result as any).data || result;
      const finalScore = (result as any).finalScore;
      const score = finalScore;
      const rewardMessage = (responseData as any).rewardMessage || (responseData as any).message || 'Cảm ơn bạn đã tham gia!';
          
      if (gameSessionId) {
        
        if (result.success) {
          // Xử lý response - kiểm tra cả data wrapper và direct response
          
          
          // Cập nhật số lần chơi từ API response
          let newRemainingAttempts = remainingAttempts - 1;
          let newCurrentAttempt = currentAttempt;
          let voucherMessage = '';
          let isNewHighScore = false;
          let previousHighScore = null;
          
          // Xử lý voucherResult từ API response
          if ((responseData as any).voucherResult?.attemptInfo) {
            const attemptInfo = (responseData as any).voucherResult.attemptInfo;
            newCurrentAttempt = attemptInfo.attemptNumber;
            newRemainingAttempts = attemptInfo.remainingAttempts;
            isNewHighScore = attemptInfo.isNewHighestScore;
            previousHighScore = attemptInfo.previousHighestScore;
            
            // Cập nhật totalAttempts nếu có
            if (attemptInfo.totalAttempts) {
              setTotalAttempts(attemptInfo.totalAttempts);
            }
            
            // Update current high score
            if (isNewHighScore) {
              // If new high score, update with the final score
              setCurrentHighScore(finalScore);
            } else if (attemptInfo.currentHighestScore !== undefined) {
              // Update with current highest score from API
              setCurrentHighScore(attemptInfo.currentHighestScore);
            } else if (previousHighScore !== null && previousHighScore > currentHighScore) {
              // Fallback: use previous high score if it's higher than current
              setCurrentHighScore(previousHighScore);
            }
            
            // Lấy thông báo từ voucherResult
            voucherMessage = (responseData as any).voucherResult.message || '';
            
            // Lưu voucher message cuối cùng nếu hết lượt chơi
            if (newRemainingAttempts === 0 && voucherMessage) {
              setFinalVoucherMessage(voucherMessage);
            }
            
            console.log('🎮 Updated attempt info from API:', {
              attemptNumber: newCurrentAttempt,
              remainingAttempts: newRemainingAttempts,
              totalAttempts: attemptInfo.totalAttempts,
              voucherMessage
            });
          } else {
            // Fallback: tính toán local
            newCurrentAttempt = currentAttempt + 1;
            newRemainingAttempts = remainingAttempts - 1;
            
            console.log('🎮 Using fallback attempt calculation:', {
              newCurrentAttempt,
              newRemainingAttempts
            });
          }
          
          setRemainingAttempts(newRemainingAttempts);
          setCurrentAttempt(newCurrentAttempt);
          
          console.log('🎮 Updated state after API response:', {
            newCurrentAttempt,
            newRemainingAttempts,
            totalAttempts
          });
          
          // Tạo thông báo chi tiết
          let notificationMessage = `Điểm số: ${finalScore}`;
          
          // Thêm thông tin high score nếu có
          if (isNewHighScore && previousHighScore !== null) {
            notificationMessage += `\n🏆 Kỷ lục mới! (Trước đó: ${previousHighScore})`;
          }
          
          // Thêm reward message
          if (rewardMessage) {
            notificationMessage += `\n\n${rewardMessage}`;
          }
          
          // Thêm voucher message nếu có
          if (voucherMessage) {
            notificationMessage += `\n\n🎁 ${voucherMessage}`;
          }
          
          // Hiển thị thông tin voucher nếu có voucher được trao
          if ((responseData as any).voucher) {
            notificationMessage += `\n\n🎫 Chúc mừng! Bạn đã nhận được voucher!`;
          }
          
          // Thêm thông tin lần chơi
          if (newRemainingAttempts > 0) {
            notificationMessage += `\n\n🎯 Lần chơi: ${newCurrentAttempt}/${totalAttempts}\n⏳ Còn lại: ${newRemainingAttempts} lần chơi`;
          } else {
            notificationMessage += `\n\n🎯 Lần chơi: ${newCurrentAttempt}/${totalAttempts}\n✅ Đã hoàn thành tất cả lần chơi!`;
          }
          
          // Hiển thị thông báo thành công
          setNotificationTitle('🎉 Hoàn thành Game!');
          setNotificationMessage(notificationMessage);
          setNotificationType('success');
          setShowNotification(true);
        } else {
          // Hiển thị thông báo lỗi
          setNotificationTitle('❌ Lỗi gửi điểm');
          setNotificationMessage(`Điểm số: ${score}\nKhông thể gửi điểm lên server.`);
          setNotificationType('error');
          setShowNotification(true);
        }
      } else {
        // Hiển thị thông báo lỗi không có gameSessionId
        setNotificationTitle('❌ Lỗi hệ thống');
        setNotificationMessage(`Điểm số: ${score}\nKhông tìm thấy gameSessionId.`);
        setNotificationType('error');
        setShowNotification(true);
      }
    } catch (error) {
      console.error('Error submitting event score:', error);
      // Hiển thị thông báo lỗi kết nối
      setNotificationTitle('❌ Lỗi kết nối');
      setNotificationMessage(`Lỗi khi gửi điểm: ${error}`);
      setNotificationType('error');
      setShowNotification(true);
    }
  };

  const handleNotificationClose = () => {
    setShowNotification(false);
    
    // Kiểm tra nếu còn lần chơi
    if (remainingAttempts > 0) {
      // Quay lại chế độ game để chơi tiếp
      setGameMode(true);
    } else {
      // Hết lần chơi, quay về trang chủ
      console.log('🚫 No more attempts remaining, navigating to home');
      navigate('/');
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const resetScanner = () => {
    setScanning(true);
    setError('');
    setJoinResult(null);
    setGameMode(false);
    setGameSessionId('');
  };

  // Nếu đang ở chế độ game
  if (gameMode && joinResult?.success && remainingAttempts > 0) {
    return (
      <div className="viewport-wrapper">
        <div className="event-game-container">
          <div className="event-game-header">
            <button className="back-button" onClick={handleBack}>
              <img src="/images/icons/back.svg" alt="Back" />
            </button>
            <div>
              <h2>{joinResult.eventInfo?.eventName || 'Event Game'}</h2>
            </div>
          </div>
          <KilobaRacingGameComponent 
            onGameEnd={handleGameEnd} 
            gameSessionId={gameSessionId}
          />
        </div>
      </div>
    );
  }

  // Nếu đã hoàn thành tất cả lần chơi
  if (gameMode && joinResult?.success && remainingAttempts === 0) {
    return (
      <div className="viewport-wrapper">
        <div className="event-qr-scan-page">
          <div className="qr-scan-header">
            <button className="back-button" onClick={handleBack}>
              <img src="/images/icons/back.svg" alt="Back" />
            </button>
            <h1>Hoàn thành Event</h1>
          </div>

          <div className="qr-scan-content">
            <div className="completion-message">
              <div className="completion-icon">🎉</div>
              <h2>Đã hoàn thành!</h2>
              <p>Bạn đã hoàn thành tất cả {totalAttempts} lần chơi trong sự kiện này.</p>
              <p>Cảm ơn bạn đã tham gia!</p>
              <div className="voucher-message">
                <h3>🎁 Kết quả quà tặng:</h3>
                <p>{finalVoucherMessage || "Bạn chưa đạt số điểm yêu cầu"}</p>
                <i>Truy cập <a href="/notifications" target="_blank" rel="noopener noreferrer">trang quà tặng</a> để xem chi tiết.</i>
              </div>
              <div className="completion-buttons">
                <button onClick={handleBack} className="back-home-button">
                  Quay về trang chủ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="viewport-wrapper">
      <div className="event-qr-scan-page">
        <div className="qr-scan-header">
          <button className="back-button" onClick={handleBack}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1>Quét mã QR Event</h1>
        </div>

        <div className="qr-scan-content">
          {(loading || (urlToken && isLoading)) && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p>{isLoading ? 'Đang kiểm tra đăng nhập...' : 'Đang tham gia sự kiện...'}</p>
            </div>
          )}

          {!scanning && !loading && !error && !urlToken && (
            <div className="scan-intro">
              <div className="qr-icon">📱</div>
              <h2>Quét mã QR sự kiện</h2>
              <p>Hướng camera về phía mã QR để tham gia sự kiện và chơi game</p>
              <button onClick={() => setScanning(true)} className="start-scan-button">
                Bắt đầu quét
              </button>
            </div>
          )}

          {error && (
            <div className="scanner-container fullscreen">
              <QRScanner
                onScanSuccess={handleScanSuccess}
                onScanError={handleScanError}
                loading={loading}
              />
              <div className="error-popup-overlay">
                <div className="error-popup">
                  <div className="error-icon">❌</div>
                  <h3>Quét mã không thành công</h3>
                  <p className="error-text">{error}</p>
                  <div className="error-actions">
                    <button onClick={resetScanner} className="retry-button">
                      Quét lại
                    </button>
                    <button onClick={handleBack} className="back-button-error">
                      Quay lại
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {scanning && !loading && !error && !urlToken && (
            <div className="scanner-container fullscreen">
              <QRScanner
                onScanSuccess={handleScanSuccess}
                onScanError={handleScanError}
                loading={loading}
              />
              <div className="scan-overlay-minimal">
                <div className="scan-instruction">
                  <p>Đặt mã QR vào trong khung</p>
                </div>
                <button className="cancel-scan" onClick={() => {
                    setScanning(false);
                    navigate(-1);
                }}>
                  Hủy
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Notification Modal */}
        <NotificationModal
          isOpen={showNotification}
          onClose={handleNotificationClose}
          title={notificationTitle}
          message={notificationMessage}
          type={notificationType}
        />

        {/* Enhanced Login Modal */}
        <EnhancedLoginModal 
          isOpen={showLoginModal} 
          onClose={() => {
            // Don't allow closing the modal if we have a pending token
            // User must login to continue
            if (!pendingToken) {
              setShowLoginModal(false);
            }
          }}
          forceLogin={!!pendingToken} // Force login if there's a pending token
        />
      </div>
    </div>
  );
};

export default EventQRScanPage;
